/**
 * The printed prescription.
 *
 * A port of `printPrescription` (doctor_dashboard.js:20348). The rebuild had no
 * template at all — the button called `window.print()` on the page, so what came
 * out of the printer was a screenshot of the dark modal, scrollbars and all.
 *
 * This builds the letterhead as a self-contained HTML document: coloured header
 * with the doctor's avatar and clinic logo, the patient/date bar, diagnosis, the
 * medicines table, boxes for lab tests and x-rays, the follow-up date, the
 * instructions block, and a footer listing every clinic with its phone and
 * hours.
 *
 * It is a pure string builder, which is the point — the legacy version could
 * only be checked by sending a page to a printer. Here the escaping and the
 * conditional sections are unit-testable.
 *
 * The header band is chosen by `doctor.prescriptionStyle` from the template
 * registry below — the four the legacy designer offered, plus four new ones.
 */

import { ARABIC_DAYS } from './dates';

const DEFAULT_PRIMARY = '#06b6d4';
const DEFAULT_SECONDARY = '#a855f7';

/**
 * Escape text for HTML.
 *
 * The legacy template interpolated diagnosis names, medicine names and free-text
 * notes straight into its markup. A patient named `<img onerror=...>` would have
 * executed in the print frame; more mundanely, a dose of "<1 tab" silently
 * vanished because the browser read it as a tag.
 */
export function escapeHtml(value) {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Escape a URL for a `src` attribute.
 *
 * Only http(s) and data: images are allowed through. `javascript:` in an `<img
 * src>` is inert in every current browser, but the doctor's logo is arbitrary
 * stored text and there is no reason for the print frame to accept a scheme it
 * cannot render.
 */
export function safeImageSrc(value) {
  const src = String(value || '').trim();
  if (!src) return '';
  if (/^data:image\//i.test(src) || /^https?:\/\//i.test(src)) return escapeHtml(src);
  return '';
}

const arabicDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return escapeHtml(value);
  return date.toLocaleDateString('ar-EG');
};

/** "17:30" -> "5:30 م". Kept local so this module prints without the app. */
const time12 = (time) => {
  const match = /^(\d{1,2}):(\d{2})/.exec(String(time || ''));
  if (!match) return '';
  const hour = Number(match[1]);
  const suffix = hour >= 12 ? 'م' : 'ص';
  const display = hour % 12 === 0 ? 12 : hour % 12;
  return `${display}:${match[2]} ${suffix}`;
};

/* ========================================================================== */

/** One clinic's block in the footer: name, phones, hours, working days. */
function clinicFooterItem(clinic, primary) {
  const phones = [clinic.mobile, clinic.landline].filter(Boolean).join(' / ');
  const days = (clinic.workingDays || [])
    .map((day) => ARABIC_DAYS[String(day).toLowerCase()] || day)
    .join('، ');

  // Hours come from either shape a clinic stores: an explicit slot list, or the
  // startTime/endTime window the clinic editor writes.
  let hours = '';
  if (clinic.startTime && clinic.endTime) {
    hours = `${time12(clinic.startTime)} - ${time12(clinic.endTime)}`;
  } else if (Array.isArray(clinic.slots) && clinic.slots.length > 0) {
    hours = `${time12(clinic.slots[0])} - ${time12(clinic.slots[clinic.slots.length - 1])}`;
  }

  return `<div style="min-width:180px;text-align:right">
    <p style="font-weight:800;font-size:15px;color:${primary};border-bottom:1px solid #e2e8f0;padding-bottom:4px;margin:0 0 6px">${escapeHtml(clinic.name)}</p>
    ${phones ? `<p style="font-size:13px;direction:ltr;font-weight:700;margin:0 0 2px">${escapeHtml(phones)}</p>` : ''}
    ${hours ? `<p style="font-size:12px;color:#475569;margin:0 0 2px"><strong>المواعيد:</strong> ${escapeHtml(hours)}</p>` : ''}
    ${days ? `<p style="font-size:12px;color:#64748b;margin:0">${escapeHtml(days)}</p>` : ''}
    ${clinic.address ? `<p style="font-size:11px;color:#94a3b8;margin:2px 0 0">${escapeHtml(clinic.address)}</p>` : ''}
  </div>`;
}

function medicinesTable(medicines, primary) {
  if (!medicines || medicines.length === 0) return '';

  const th = `background:#f0fdfa;color:${primary};padding:10px 12px;text-align:right;border-bottom:2px solid ${primary};font-size:12px`;
  const td = 'padding:8px 12px;border-bottom:1px solid #e5e7eb;color:#0f172a';

  return `
    <h3 style="font-size:14px;color:${primary};border-bottom:1px solid #e2e8f0;padding-bottom:5px;margin:0 0 8px">الأدوية</h3>
    <table style="width:100%;border-collapse:collapse;margin-bottom:20px">
      <thead>
        <tr>
          <th style="${th};width:40px;text-align:center">#</th>
          <th style="${th}">اسم الدواء</th>
          <th style="${th}">الجرعة</th>
          <th style="${th}">عدد المرات</th>
          <th style="${th}">التوقيت</th>
          <th style="${th}">المدة</th>
          <th style="${th}">ملاحظات</th>
        </tr>
      </thead>
      <tbody>
        ${medicines
          .map(
            (medicine, index) => `
        <tr>
          <td style="${td};text-align:center;font-weight:bold;color:${primary}">${index + 1}</td>
          <td style="${td};font-weight:bold">${escapeHtml(medicine.name)}</td>
          <td style="${td}">${escapeHtml(medicine.dosage)}</td>
          <td style="${td}">${escapeHtml(medicine.frequency)}</td>
          <td style="${td}">${escapeHtml(medicine.timing)}</td>
          <td style="${td}">${escapeHtml(medicine.duration)}</td>
          <td style="${td};font-size:11px;color:#6b7280">${escapeHtml(medicine.note || medicine.notes)}</td>
        </tr>`,
          )
          .join('')}
      </tbody>
    </table>`;
}

/** A bordered list — the lab-test and x-ray boxes share this shape. */
function requestBox({ items, title, icon, borderColor, titleColor }) {
  if (!items || items.length === 0) return '';
  return `
    <div class="rx-block" style="margin-top:14px;padding:12px;border:1px solid ${borderColor};border-radius:8px">
      <h4 style="color:${titleColor};margin:0 0 8px;font-size:14px">${icon} ${escapeHtml(title)}</h4>
      <ul style="margin:0;padding:0 20px;color:#1e293b;font-weight:600">
        ${items
          .map(
            (item) =>
              `<li style="margin:4px 0">${escapeHtml(item.name)}${
                item.note
                  ? ` - <span style="color:#64748b;font-size:12px">${escapeHtml(item.note)}</span>`
                  : ''
              }</li>`,
          )
          .join('')}
      </ul>
    </div>`;
}

/**
 * Files uploaded against a lab test or x-ray on this prescription.
 *
 * Same precedence the uploader writes and the screen reads: a legacy single
 * `resultUrl` string, then a `results` array of `{ data, name }` objects. The
 * payload key is `data`.
 */
export function collectPrescriptionResults(prescription) {
  const out = [];

  const push = (item, kind) => {
    const files = [];
    if (item?.resultUrl) files.push({ data: item.resultUrl, name: 'نتيجة سابقة' });
    if (Array.isArray(item?.results)) files.push(...item.results);
    if (Array.isArray(item?.resultFiles)) files.push(...item.resultFiles);

    files.forEach((file, index) => {
      const src =
        typeof file === 'string' ? file : file?.data || file?.dataUri || file?.url || file?.src;
      if (!src) return;
      out.push({
        src,
        kind,
        itemName: item?.name || '',
        label: (typeof file === 'object' && (file.name || file.label)) || '',
        uploadedAt: (typeof file === 'object' && (file.uploadedAt || file.date)) || null,
        doctorComment: item?.doctorComment || '',
        key: `${kind}-${item?.name || ''}-${index}`,
      });
    });
  };

  (prescription?.labTests || []).forEach((item) => push(item, 'lab'));
  (prescription?.xrays || []).forEach((item) => push(item, 'xray'));

  return out;
}

/**
 * The results pages that follow the prescription itself.
 *
 * A prescription that requested a scan is incomplete without the scan. Anyone
 * opening the file later — another doctor, an insurer, the patient — should see
 * the result attached to the request that produced it, in one document, rather
 * than being told that a file exists somewhere in the app.
 *
 * Non-image files cannot be drawn onto paper, so they are listed by name
 * instead of disappearing.
 */
function resultsPages(prescription, primary) {
  const all = collectPrescriptionResults(prescription);
  if (all.length === 0) return '';

  const images = all.filter((r) => {
    const src = String(r.src);
    return /^data:image\//i.test(src) || Boolean(safeImageSrc(src));
  });
  const others = all.filter((r) => !images.includes(r));

  const kindLabel = (kind) => (kind === 'xray' ? 'أشعة' : 'تحليل');
  const tone = (kind) => (kind === 'xray' ? '#d97706' : '#9333ea');

  const figures = images
    .map(
      (result) => `
    <figure class="rx-block" style="margin:0 0 14px;border:1px solid ${tone(result.kind)}33;border-radius:12px;padding:10px;background:${tone(result.kind)}08">
      <figcaption style="display:flex;justify-content:space-between;align-items:center;gap:8px;margin-bottom:8px;font-size:12px">
        <span style="font-weight:800;color:#0f172a">${escapeHtml(result.itemName || kindLabel(result.kind))}${
          result.label && result.label !== result.itemName
            ? ` <span style="font-weight:400;color:#94a3b8">— ${escapeHtml(result.label)}</span>`
            : ''
        }</span>
        <span style="display:inline-block;padding:2px 9px;border-radius:999px;font-size:10.5px;font-weight:700;color:${tone(result.kind)};background:${tone(result.kind)}17;border:1px solid ${tone(result.kind)}38;white-space:nowrap">${escapeHtml(kindLabel(result.kind))}${
          result.uploadedAt ? ` · ${escapeHtml(String(result.uploadedAt).slice(0, 10))}` : ''
        }</span>
      </figcaption>
      <img src="${safeImageSrc(result.src) || escapeHtml(result.src)}" alt="${escapeHtml(result.itemName)}" style="width:100%;max-height:330px;object-fit:contain;background:#f8fafc;border-radius:8px;border:1px solid #eef2f7">
      ${
        result.doctorComment
          ? `<p style="margin:8px 0 0;font-size:11px;color:#475569;background:white;border-right:3px solid ${tone(result.kind)};border-radius:6px;padding:6px 9px">تعليق الطبيب: ${escapeHtml(result.doctorComment)}</p>`
          : ''
      }
    </figure>`,
    )
    .join('');

  const listed =
    others.length > 0
      ? `<div class="rx-block" style="border:1px dashed #cbd5e1;border-radius:10px;padding:10px 14px;font-size:11.5px;color:#475569">
      <p style="margin:0 0 4px;font-weight:700;color:#0f172a">مرفقات غير قابلة للطباعة</p>
      <ul style="margin:0;padding:0 18px">
        ${others.map((r) => `<li style="margin:2px 0">${escapeHtml(kindLabel(r.kind))}: ${escapeHtml(r.itemName || r.label)}</li>`).join('')}
      </ul>
    </div>`
      : '';

  return `
    <div style="break-before:page;page-break-before:always;padding:10px 40px 10px">
      <h3 style="font-size:15px;color:${primary};border-bottom:2px solid ${primary};padding-bottom:6px;margin:0 0 14px">النتائج المرفقة بهذه الروشتة</h3>
      ${figures}
      ${listed}
    </div>`;
}

/* ==========================================================================
   Letterhead templates

   The legacy designer offered four (`wave-top`, `wave-sides`, `diagonal` and
   `wave-full`), though only the first three were ever drawn — `wave-full` fell
   through to the default branch, so picking it silently gave you `wave-top`.
   All four are real here, plus four more.

   Each entry renders only the header band. Everything below it — the patient
   bar, the content, the footer — is shared, so a new design is one function
   rather than a copy of the whole sheet.
   ========================================================================== */

/** Every template, in the order the picker shows them. */
export const PRESCRIPTION_TEMPLATES = [
  { id: 'wave-top', label: 'موجة علوية', group: 'legacy' },
  { id: 'wave-sides', label: 'موجة جانبية', group: 'legacy' },
  { id: 'diagonal', label: 'قطري', group: 'legacy' },
  { id: 'wave-full', label: 'هيدر كامل', group: 'legacy' },
  { id: 'minimal', label: 'بسيط', group: 'new' },
  { id: 'classic', label: 'كلاسيكي', group: 'new' },
  { id: 'ribbon', label: 'شريط', group: 'new' },
  { id: 'arch', label: 'قوس', group: 'new' },
];

export const DEFAULT_TEMPLATE = 'wave-top';

export const isKnownTemplate = (id) =>
  PRESCRIPTION_TEMPLATES.some((template) => template.id === id);

/**
 * Page padding per template.
 *
 * The bottom value used to be 220px to clear the footer. It no longer needs to
 * be: the footer sits in a <tfoot> and reserves its own space, and 220px of
 * padding on top of that pushed a one-page prescription onto a second sheet.
 */
const BODY_PADDING = {
  'wave-top': '20px 40px 24px',
  'wave-full': '20px 40px 24px',
  minimal: '28px 40px 24px',
  classic: '24px 40px 24px',
};

export const bodyPaddingFor = (style) => BODY_PADDING[style] || '20px 36px 24px';

/**
 * Render the header band for a template.
 *
 * @param {string} style
 * @param {{primary, secondary, avatarBox, logoBox, headerText}} ctx
 *        `headerText(onColour)` renders the name/specialty/clinic stack in
 *        either the light or the dark treatment.
 */
export function renderHeader(style, ctx) {
  const { primary, secondary, avatarBox, logoBox, headerText } = ctx;

  switch (style) {
    case 'wave-sides':
      return `
    <div style="position:relative;overflow:hidden;min-height:130px;background:white">
      <svg viewBox="0 0 794 130" style="position:absolute;top:0;left:0;width:100%;height:130px" preserveAspectRatio="none">
        <path d="M0 0 L500 0 Q420 65 500 130 L0 130 Z" fill="${primary}"/>
        <path d="M0 0 L480 0 Q400 65 480 130 L0 130 Z" fill="${secondary}" opacity=".25"/>
      </svg>
      <div style="position:relative;z-index:2;padding:22px 36px;display:flex;align-items:center;justify-content:space-between;min-height:130px">
        <div style="display:flex;align-items:center;gap:14px">${avatarBox}${headerText(false)}</div>
        ${logoBox}
      </div>
    </div>`;

    case 'diagonal':
      return `
    <div style="position:relative;min-height:130px;background:${primary};overflow:hidden">
      <svg viewBox="0 0 794 130" style="position:absolute;top:0;left:0;width:100%;height:130px" preserveAspectRatio="none">
        <path d="M794 0 L794 130 L500 130 L600 0 Z" fill="white" opacity=".12"/>
        <path d="M794 0 L794 130 L420 130 L520 0 Z" fill="${secondary}" opacity=".2"/>
      </svg>
      <div style="position:relative;z-index:2;padding:22px 36px;display:flex;align-items:center;justify-content:space-between;min-height:130px">
        ${headerText(true)}
        <div style="display:flex;align-items:center;gap:10px;flex-shrink:0">${logoBox}${avatarBox}</div>
      </div>
    </div>`;

    // A solid band with one soft wave inside it — the design the legacy picker
    // offered but never actually drew.
    case 'wave-full':
      return `
    <div style="position:relative;background:${primary};overflow:hidden;min-height:140px">
      <svg viewBox="0 0 794 140" style="position:absolute;top:0;left:0;width:100%;height:140px" preserveAspectRatio="none">
        <path d="M0 96 Q200 140 400 108 Q600 76 794 124 L794 140 L0 140 Z" fill="${secondary}" opacity=".38"/>
        <circle cx="120" cy="34" r="70" fill="white" opacity=".07"/>
      </svg>
      <div style="position:relative;z-index:2;padding:26px 40px;display:flex;justify-content:space-between;align-items:center;min-height:140px">
        <div style="display:flex;align-items:center;gap:14px">${avatarBox}${headerText(true)}</div>
        ${logoBox}
      </div>
    </div>`;

    // No fill at all: two rules and the name. Cheapest to print, and the one
    // most clinics with pre-printed paper actually want.
    case 'minimal':
      return `
    <div style="background:white;padding:26px 40px 0">
      <div style="display:flex;justify-content:space-between;align-items:flex-end;gap:16px;padding-bottom:14px;border-bottom:3px solid ${primary}">
        <div style="display:flex;align-items:center;gap:14px">${avatarBox}${headerText(false)}</div>
        ${logoBox}
      </div>
      <div style="height:2px;background:${secondary};opacity:.45;margin-top:3px"></div>
    </div>`;

    // Centred, symmetric, with the logo above the name — the shape of a
    // traditional printed letterhead.
    case 'classic':
      return `
    <div style="background:white;padding:26px 40px 0;text-align:center">
      ${logoBox ? `<div style="display:flex;justify-content:center;margin-bottom:8px">${logoBox}</div>` : ''}
      <div style="display:inline-block;text-align:center">${headerText(false, true)}</div>
      <div style="margin-top:14px;border-top:3px double ${primary};padding-top:3px">
        <div style="height:1px;background:${secondary};opacity:.5"></div>
      </div>
    </div>`;

    // A band that stops short of the edge, with a folded corner.
    case 'ribbon':
      return `
    <div style="position:relative;background:white;padding-top:18px">
      <div style="position:relative;background:${primary};margin-left:38px;padding:20px 40px;display:flex;justify-content:space-between;align-items:center;border-radius:0 0 0 18px">
        <div style="display:flex;align-items:center;gap:14px">${avatarBox}${headerText(true)}</div>
        ${logoBox}
        <div style="position:absolute;bottom:-14px;right:0;border-left:14px solid transparent;border-top:14px solid ${secondary}"></div>
      </div>
    </div>`;

    // A wide rounded arch, cropped by the page edge.
    case 'arch':
      return `
    <div style="position:relative;overflow:hidden;background:white;min-height:150px">
      <svg viewBox="0 0 794 150" style="position:absolute;top:0;left:0;width:100%;height:150px" preserveAspectRatio="none">
        <path d="M0 0 L794 0 L794 92 Q397 178 0 92 Z" fill="${primary}"/>
        <path d="M0 0 L794 0 L794 70 Q397 146 0 70 Z" fill="${secondary}" opacity=".28"/>
      </svg>
      <div style="position:relative;z-index:2;padding:24px 40px 40px;display:flex;justify-content:space-between;align-items:center">
        <div style="display:flex;align-items:center;gap:14px">${avatarBox}${headerText(true)}</div>
        ${logoBox}
      </div>
    </div>`;

    // wave-top — the default, and what the legacy sheets shipped with.
    default:
      return `
    <div style="background:${primary};position:relative;overflow:hidden">
      <svg viewBox="0 0 794 50" style="position:absolute;bottom:-1px;left:0;width:100%;z-index:1" preserveAspectRatio="none">
        <path d="M0 50 Q200 0 400 30 Q600 60 794 10 L794 50 Z" fill="white"/>
      </svg>
      <svg viewBox="0 0 794 80" style="position:absolute;top:0;left:0;width:100%;opacity:.2;z-index:0" preserveAspectRatio="none">
        <path d="M0 0 Q200 80 400 40 Q600 0 794 60 L794 0 Z" fill="${secondary}"/>
      </svg>
      <div style="position:relative;z-index:2;padding:24px 40px 50px;display:flex;justify-content:space-between;align-items:center">
        <div style="display:flex;align-items:center;gap:14px">${avatarBox}${headerText(true)}</div>
        ${logoBox}
      </div>
    </div>`;
  }
}

/* ========================================================================== */

/**
 * Build the printable document for one prescription.
 *
 * @param {object}   args
 * @param {object}   args.prescription
 * @param {object}   args.doctor         normalised doctor (name, title, clinics, printColors)
 * @param {object}   [args.clinic]       the visit's clinic; falls back to the doctor's first
 * @param {string}   [args.patientName]
 * @param {string}   [args.patientCode]
 * @returns {string} a complete HTML document
 */
export function buildPrescriptionHtml({
  prescription,
  doctor,
  clinic = null,
  patientName = '',
  patientCode = '',
}) {
  if (!prescription) throw new Error('buildPrescriptionHtml: prescription is required');
  if (!doctor) throw new Error('buildPrescriptionHtml: doctor is required');

  const primary = doctor.printColors?.primary || DEFAULT_PRIMARY;
  const secondary = doctor.printColors?.secondary || DEFAULT_SECONDARY;
  // Normalise once, so an unrecognised value falls back completely rather than
  // picking up the default header with another template's page padding.
  const requested = doctor.prescriptionStyle || DEFAULT_TEMPLATE;
  const style = isKnownTemplate(requested) ? requested : DEFAULT_TEMPLATE;

  const clinics = Array.isArray(doctor.clinics) ? doctor.clinics : [];
  const visitClinic = clinic || clinics[0] || null;

  const logo = safeImageSrc(doctor.logo);
  const avatar = safeImageSrc(doctor.image);

  const name = escapeHtml(patientName || prescription.patientName || 'مريض');
  const code = escapeHtml(patientCode || prescription.manualPatientCode || '');
  const specialty = escapeHtml(doctor.title || doctor.categoryName || 'استشاري');
  const clinicName = escapeHtml(visitClinic?.name || '');
  const doctorName = escapeHtml(doctor.name || '');

  /* --- header pieces --------------------------------------------------- */

  // The logo sits directly on the header with no plate behind it. The legacy
  // template wrapped it in a white rounded box, which put a bright rectangle in
  // the corner of every sheet and defeated the point of uploading a logo with a
  // transparent background.
  const logoBox = logo
    ? `<img src="${logo}" alt="" style="width:76px;height:76px;object-fit:contain;flex-shrink:0">`
    : '';

  const avatarBox = avatar
    ? `<div style="width:68px;height:68px;border-radius:50%;overflow:hidden;border:3px solid rgba(255,255,255,.8);flex-shrink:0"><img src="${avatar}" alt="" style="width:100%;height:100%;object-fit:cover"></div>`
    : `<div style="width:68px;height:68px;border-radius:50%;background:rgba(255,255,255,.2);display:flex;align-items:center;justify-content:center;font-size:28px;flex-shrink:0">🩺</div>`;

  // A faint centred logo behind the page, as the legacy sheet had.
  const watermark = logo
    ? `<div style="position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);width:70%;opacity:.03;pointer-events:none;z-index:0"><img src="${logo}" alt="" style="width:100%;height:auto;object-fit:contain"></div>`
    : '';

  const headerText = (onColour, centred = false) => `
    <div style="text-align:${centred ? 'center' : 'right'}">
      <h1 style="font-size:26px;font-weight:900;color:${onColour ? '#ffffff' : '#0f172a'};margin:0">د/ ${doctorName}</h1>
      <p style="font-size:13px;color:${onColour ? 'rgba(255,255,255,.85)' : '#475569'};margin:4px 0 0">${specialty}</p>
      ${clinicName ? `<p style="font-size:11px;color:${onColour ? 'rgba(255,255,255,.65)' : '#94a3b8'};margin:3px 0 0">${clinicName}</p>` : ''}
    </div>`;

  const header = renderHeader(style, {
    primary,
    secondary,
    avatarBox,
    logoBox,
    headerText,
  });

  /* --- body ------------------------------------------------------------- */

  const patientBar = `
    <div class="rx-block" style="display:flex;justify-content:space-between;align-items:center;background:#f8fafc;border:1px solid #e2e8f0;border-radius:10px;padding:12px 18px;margin-bottom:28px">
      <div style="display:flex;align-items:center;gap:14px">
        <div>
          <span style="color:#64748b;margin-left:6px">اسم المريض :</span>
          <span style="font-weight:800;font-size:20px">${name}</span>
        </div>
        ${code ? `<div style="background:#e2e8f0;color:#475569;font-size:13px;font-weight:bold;padding:3px 8px;border-radius:6px;font-family:monospace" dir="ltr">${code}</div>` : ''}
      </div>
      <div>
        <span style="color:#64748b;margin-left:6px">التاريخ :</span>
        <span style="font-weight:700">${arabicDate(prescription.date)}</span>
      </div>
    </div>`;

  const content = `
    <div style="min-height:380px;margin-bottom:30px;padding:0 15px">
      ${
        prescription.diagnosis
          ? `<div style="margin-bottom:18px">
        <h3 style="font-size:14px;color:${primary};border-bottom:1px solid #e2e8f0;padding-bottom:5px;margin:0">التشخيص</h3>
        <p style="margin:5px 0;font-size:15px;color:#1e293b;font-weight:600">${escapeHtml(prescription.diagnosis)}</p>
      </div>`
          : ''
      }
      ${medicinesTable(prescription.medicines, primary)}
      ${requestBox({
        items: prescription.labTests,
        title: 'التحاليل المطلوبة',
        icon: '📋',
        borderColor: '#a855f7',
        titleColor: '#7c3aed',
      })}
      ${requestBox({
        items: prescription.xrays,
        title: 'الأشعة المطلوبة',
        icon: '🔬',
        borderColor: '#f59e0b',
        titleColor: '#d97706',
      })}
      ${
        prescription.followUpDate
          ? `<div class="rx-block" style="margin-top:12px;padding:10px;background:#ecfdf5;border-radius:8px;border:1px solid #10b981">
        <strong style="color:#047857">📅 موعد المتابعة القادم:</strong> ${escapeHtml(
          String(prescription.followUpDate).replace('T', ' الساعة '),
        )}
      </div>`
          : ''
      }
      ${
        prescription.notes
          ? `<div class="rx-block" style="margin-top:12px;padding:12px;background:#fffbeb;border-radius:8px;border:1px solid #fbbf24">
        <h4 style="color:#92400e;margin:0 0 5px;font-size:13px">⚠️ تعليمات هامة</h4>
        <p style="margin:0;font-size:13px;color:#78350f;font-weight:600;white-space:pre-line">${escapeHtml(prescription.notes)}</p>
      </div>`
          : ''
      }
    </div>`;

  const footer = `
    <div class="rx-footer" style="border-top:2px solid ${primary};background:white;padding:12px 40px 8px;margin-top:14px">
      <div style="display:flex;flex-wrap:wrap;gap:24px;justify-content:space-between;margin-bottom:12px">
        ${clinics.map((c) => clinicFooterItem(c, primary)).join('')}
      </div>
      <p style="text-align:center;font-size:13px;font-weight:600;color:#94a3b8;letter-spacing:.5px;margin:0">Powered by Capsule Medical System</p>
    </div>`;

  const padding = bodyPaddingFor(style);

  // Uploaded lab and x-ray results, on pages after the prescription itself.
  const resultsSection = resultsPages(prescription, primary);

  return `<!DOCTYPE html>
<html dir="rtl" lang="ar">
<head>
<meta charset="utf-8">
<title>وصفة طبية - ${name}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Noto+Kufi+Arabic:wght@400;600;700;900&display=swap" rel="stylesheet">
<style>
  * { box-sizing: border-box; }
  body {
    margin: 0;
    font-family: 'Noto Kufi Arabic', Tahoma, Arial, sans-serif;
    background: white;
    color: #0f172a;
  }
  /* A bordered block must not be split by a page boundary. Without this the
     browser happily draws the top half of an instructions box at the foot of one
     page and its text at the head of the next, which reads as an empty box
     followed by orphaned text. */
  .rx-block,
  .rx-footer,
  tr {
    break-inside: avoid;
    page-break-inside: avoid;
  }

  /* The footer lives in a <tfoot>, which is the one construct browsers both
     repeat on every printed page AND reserve vertical space for. A fixed
     element does neither — it repeats, but it paints over the content instead
     of pushing it down, which is why the instructions box ended up sitting on
     top of the clinic details. */
  .rx-sheet {
    width: 100%;
    border-collapse: collapse;
  }
  .rx-sheet > tfoot { display: table-footer-group; }
  .rx-sheet > tbody > tr > td,
  .rx-sheet > tfoot > tr > td {
    padding: 0;
    vertical-align: top;
  }

  @media print {
    /* Without print-color-adjust the header prints as a white band — every
       browser strips background colours from printed pages by default, and the
       letterhead is the one thing that must survive. */
    /* Page one is full bleed so the letterhead runs to the paper edge. Every
       page after it gets a top margin: without one, content that spilled over
       — the instructions box, the attached results — started hard against the
       edge of the sheet with no breathing room above it.
       The tfoot reserves the bottom, so no bottom margin is needed. */
    @page { size: A4; margin: 14mm 0 0 0; }
    @page :first { margin: 0; }
    body {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
  }

</style>
</head>
<body>
  <div style="position:relative;background:white" dir="rtl">
    ${watermark}
    <table class="rx-sheet">
      <tfoot><tr><td>${footer}</td></tr></tfoot>
      <tbody><tr><td>
        ${header}
        <div style="position:relative;z-index:1;padding:${padding}">
          ${patientBar}
          ${content}
        </div>
        ${resultsSection}
      </td></tr></tbody>
    </table>
  </div>
</body>
</html>`;
}

export default buildPrescriptionHtml;
